import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getCandidateStatusColor } from "@/utils/statusColors";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  FileText,
  Database,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Tables } from "@/integrations/supabase/types";
import { format } from "date-fns";
import { getCandidateStatusLabel } from "@/types/enums";
import ReactMarkdown from "react-markdown"; // Import ReactMarkdown
import remarkGfm from "remark-gfm"; // Import remarkGfm for GitHub Flavored Markdown
import { useAuthorization } from "@/hooks/useAuthorization";
import { USER_ROLES } from "@/types/enums";

type Candidate = Tables<"candidates"> & { joining_date?: string | null };

interface CandidateBasicInfoProps {
  candidate: Candidate;
}

// Helper function to render parsed resume data
const renderParsedResumeData = (data: any) => {
  if (!data) {
    return (
      <div className="text-sm text-gray-500 italic">
        No parsed resume data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Basic Information
          </h4>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">Name:</span>{" "}
              {data.full_name || "N/A"}
            </p>
            <p>
              <span className="font-medium">Email:</span> {data.email || "N/A"}
            </p>
            <p>
              <span className="font-medium">Phone:</span> {data.phone || "N/A"}
            </p>
            <p>
              <span className="font-medium">CGPA:</span> {data.cgpa || "N/A"}
            </p>
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Education
          </h4>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">Qualification:</span>{" "}
              {data.qualification || "N/A"}
            </p>
            <p>
              <span className="font-medium">Branch:</span>{" "}
              {data.branch || "N/A"}
            </p>
            <p>
              <span className="font-medium">College:</span>{" "}
              {data.college_university || "N/A"}
            </p>
          </div>
        </div>
      </div>

      {data.summary && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">Summary</h4>
          <p className="text-sm text-gray-600">{data.summary}</p>
        </div>
      )}

      {data.key_skills && data.key_skills.length > 0 && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Key Skills
          </h4>
          <div className="flex flex-wrap gap-2">
            {data.key_skills.map((skill: string, index: number) => (
              <Badge key={index} variant="secondary">
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {data.technologies && data.technologies.length > 0 && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Technologies
          </h4>
          <div className="flex flex-wrap gap-2">
            {data.technologies.map((tech: string, index: number) => (
              <Badge key={index} variant="outline">
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export function CandidateBasicInfo({ candidate }: CandidateBasicInfoProps) {
  const { role } = useAuthorization();
  return (
    <Card>
      <CardHeader className="flex flex-row justify-between items-center">
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Candidate Information
        </CardTitle>
        <div>
          {candidate.resume_url && (
            <div className="">
              <a
                href={candidate.resume_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                View Original Resume PDF
              </a>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="parsed">Parsed Data</TabsTrigger>
            <TabsTrigger value="resume">Resume</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-500" />
                <span className="text-sm">{candidate.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-500" />
                <span className="text-sm">
                  {candidate.phone || "Not provided"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span className="text-sm">
                  Permanent City: {candidate.permanent_city || "Not provided"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span className="text-sm">
                  Current City: {candidate.current_city || "Not provided"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-gray-500" />
                <span className="text-sm">
                  {candidate.position_applied || "Not specified"}
                </span>
              </div>
              {role === USER_ROLES.HR && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">
                    {candidate.joining_date
                      ? format(new Date(candidate.joining_date), "MMM dd, yyyy")
                      : "Joining date not set"}
                  </span>
                </div>
              )}
              <div className="pt-2">
                <Badge className={getCandidateStatusColor(candidate.status)}>
                  {getCandidateStatusLabel(candidate.status as any)}
                </Badge>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="parsed" className="space-y-4">
            <div className="max-h-64 overflow-y-auto">
              {renderParsedResumeData(candidate.parsed_resume_data)}
            </div>
          </TabsContent>

          <TabsContent value="resume" className="space-y-4">
            {candidate.resume_markdown ? (
              <div className="prose-sm dark:prose-invert max-w-none bg-gray-50 p-4 rounded-lg border max-h-64 overflow-y-auto">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {candidate.resume_markdown}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                No resume markdown available
              </p>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
